import { useQuery } from '@tanstack/react-query';
import './EditCertificationModal.scss';
import { Chip, Input, LoadingPlaceholder, Modal, Select } from '@trackman/web-shared-components';
import Subtitle from 'components/TextComps/Subtitle';
import { useServices } from 'index';
import { useState } from 'react';
import { AdminCertification } from 'types/admin/modules';

type Props = {
  open: boolean;
  certification: AdminCertification;
  newCertifications: AdminCertification[];
  onClose: (certification: (AdminCertification & {newId: string}) | undefined) => void;
};

export const EditCertificationModal = ({ open, certification, newCertifications, onClose }: Props) => {
  const service = useServices().editCertificationService;
  const { data, isPending } = useQuery({
    queryKey: ['availablePrerequisites', certification.id],
    queryFn: () => service.getAvailablePrerequisites(certification.id),
    enabled: !!certification.id,
    staleTime: Infinity,
  });

  const [certificationTitle, setCertificationTitle] = useState<string | undefined>(certification.title ?? undefined);
  const [area9Entrypoint, setArea9Entrypoint] = useState<string | undefined>(certification.enterpoint ?? undefined);
  const [prerequisiteIds, setPrerequisiteIds] = useState<string[] | undefined>(certification.prerequisites ?? undefined);
  const [shortDescription, setShortDescription] = useState<string | undefined>(certification.shortDescription ?? undefined);
  const [longDescription, setLongDescription] = useState<string | undefined>(certification.fullDescription ?? undefined);
  const [initialId, setInitialId] = useState<string | undefined>(certification.id ?? undefined);
  const [newId, setNewId] = useState<string | undefined>(certification.id ?? undefined);

  const handleClose = () => {
    resetLocalState();
    onClose(undefined);
  };
  const handleSaveCertification = () => {
    const payload: AdminCertification & {newId: string} = {
      ...certification,
      title: certificationTitle ?? '',
      enterpoint: area9Entrypoint ?? '',
      prerequisites: prerequisiteIds ?? [],
      shortDescription: shortDescription ?? '',
      fullDescription: longDescription ?? '',
      id: initialId ?? '',
      newId: newId ?? '',
    };
    onClose(payload);
    resetLocalState();
  };

  const getPrerequisiteName = (id: string) => {
    return getPrerequisitesList()?.find((c) => c.id === id)?.title;
  };

  const getPrerequisitesList = () => {
    if (!newCertifications && !data) {
      return [];
    } else if (!newCertifications) {
      return data;
    } else if (!data) {
      return newCertifications;
    } else {
      return [...newCertifications, ...data];
    }
  };

  const resetLocalState = () => {
    setCertificationTitle(undefined);
    setArea9Entrypoint(undefined);
    setPrerequisiteIds([]);
    setShortDescription(undefined);
    setLongDescription(undefined);
    setInitialId(undefined);
    setNewId(undefined);
  };

  const shouldDisableSaveButton = !certificationTitle || !area9Entrypoint || !shortDescription;

  return (
    <Modal
      className='edit-certification-modal'
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: 'Save',
        onClick: handleSaveCertification,
        disabled: shouldDisableSaveButton,
      }}
      onClose={handleClose}
      title={certificationTitle ? certificationTitle : 'Edit certification'}
      open={open}
    >
      {!isPending ? (
        <>
          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>General Information</>
            </Subtitle>
            <div>
              <Input
                label='Certification Title'
                value={certificationTitle}
                required
                onChange={(e) => setCertificationTitle(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />

              <Input
                label='Area9 Entrypoint'
                value={area9Entrypoint}
                required
                onChange={(e) => setArea9Entrypoint(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            <div className='prerequisites-wrapper'>
              <Select
                label='Prerequisites'
                value={prerequisiteIds}
                hint='Certifications required before completing this one.'
                multiple
                selectOptions={getPrerequisitesList()?.map((course) => {
                  return {
                    label: course.title,
                    value: course.id,
                  };
                })}
                onChange={(e) => setPrerequisiteIds(e.target.value as string[])}
              />
              {prerequisiteIds && prerequisiteIds?.length > 0 ? (
                <div className='prerequisites'>
                  {prerequisiteIds?.map((id) => (
                    <Chip
                      className='prerequisite'
                      color='primary'
                      key={id}
                      label={getPrerequisiteName(id) ?? ''}
                      onDelete={() => setPrerequisiteIds(prerequisiteIds.filter((prerequisiteId) => id !== prerequisiteId))}
                    />
                  ))}
                </div>
              ) : (
                <></>
              )}
            </div>
            <Input
              label='Existing certificate id'
              value={newId}
              onChange={(e) => setNewId(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              className='exisiting-certificate-id'
            />
          </div>

          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>About the Certification</>
            </Subtitle>
            <Input
              label='Short Description'
              required
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value as string)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
            {/* <TextArea
              label='About this certification'
              required
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value as string)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            /> */}
          </div>

          {/* <div className='edit-certification-modal-section'>
            <Subtitle>
              <>What you'll get</>
            </Subtitle>
            <ul>
              <li>Download your certificate as a PDF and print it</li>
              <li>Add it to your professional portfolio or resume</li>
              <li>Frame it for instant bragging rights!</li>
            </ul>
          </div>

          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>Exam details</>
            </Subtitle>
            <ul>
              <li>
                $250 <span>(Get 50% off with Premium)</span>
              </li>
              <li>3 attemps</li>
              <li>{certification.numberOfQuestions ?? 0} questions</li>
              <li>
                {prerequisiteIds && prerequisiteIds?.length > 0
                  ? `Prerequisites for this certification: ${prerequisiteIds.map((id) => getPrerequisiteName(id)).join(', ')}`
                  : 'No prerequisites needed'}
              </li>
            </ul>
          </div> */}
        </>
      ) : (
        <LoadingPlaceholder />
      )}
    </Modal>
  );
};
